import { useMemo, useState } from 'react';
import { ArrowsClockwiseIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Modal } from '@/components/Modal';
import { Table } from '@/components/Table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import { WebhookDelivery, WebhookDeliveryStatus } from '@/types/alert';
import { TableColumn } from '@/types/table';
import { cn } from '@/lib/utils';
import { toUtcTimestamp } from '@/utils/date';
import { formatLatency } from '@/utils/time';
import {
    DateTimeMode,
    getDateRangeFromMode,
    type DateTimeFilter,
} from '@/utils/query-filters';
import { WebhookDeliveryDetails } from './WebhookDeliveryDetails';
import { WebhookDeliveryEventCell } from './WebhookDeliveryEventCell';
import { WebhookDeliveryFilters } from './WebhookDeliveryFilters';
import { WebhookDeliveryTimeCell } from './WebhookDeliveryTimeCell';
import { findFirstDeliveriesToNewUrl } from './webhook-delivery-payload';
import { WebhookDeliverySummary } from './WebhookDeliverySummary';

const PAGE_SIZE = 10;
const DEFAULT_DATE_TIME: DateTimeFilter = { mode: DateTimeMode.LAST_HOUR };

interface WebhookDeliveryLogModalProps {
    alertRuleId: string;
    alertRuleActionId: string;
    alertRuleName: string;
    webhookUrl: string;
    onClose: () => void;
}

export function WebhookDeliveryLogModal({
    alertRuleId,
    alertRuleActionId,
    alertRuleName,
    webhookUrl,
    onClose,
}: WebhookDeliveryLogModalProps) {
    const { t } = useTranslation([
        I18nNamespace.ALERT_RULES,
        I18nNamespace.COMMON,
    ]);
    const [page, setPage] = useState(1);
    const [statuses, setStatuses] = useState<WebhookDeliveryStatus[]>([]);
    const [dateTime, setDateTime] = useState<DateTimeFilter>(DEFAULT_DATE_TIME);
    const [rangeAnchor, setRangeAnchor] = useState(() => Date.now());
    const isCustomRange = dateTime.mode === DateTimeMode.CUSTOM;

    const resolvedRange = useMemo(
        () =>
            isCustomRange && dateTime.customRange
                ? dateTime.customRange
                : getDateRangeFromMode(dateTime.mode),
        [dateTime, isCustomRange, rangeAnchor],
    );

    const startTime = resolvedRange
        ? toUtcTimestamp(resolvedRange.startDate)
        : undefined;
    const endTime =
        resolvedRange && isCustomRange
            ? toUtcTimestamp(resolvedRange.endDate)
            : undefined;
    const hasAnyFilter = statuses.length > 0 || !!resolvedRange;

    const { data: stats, mutate: refreshStats } = useSWR(
        [
            'webhook-delivery-stats',
            alertRuleId,
            alertRuleActionId,
            startTime,
            endTime,
        ],
        () =>
            EngineService.getWebhookDeliveryStats(
                alertRuleId,
                alertRuleActionId,
                {
                    startTime,
                    endTime,
                },
            ),
        { keepPreviousData: true, revalidateOnFocus: false },
    );

    const refresh = () => {
        setRangeAnchor(Date.now());
        refreshStats();
        refreshDeliveries();
    };

    const changeDateTime = (next: DateTimeFilter) => {
        setDateTime(next);
        setPage(1);
    };

    const toggleStatus = (status: WebhookDeliveryStatus) => {
        setStatuses((previous) =>
            previous.includes(status)
                ? previous.filter((existing) => existing !== status)
                : [...previous, status],
        );
        setPage(1);
    };

    const {
        data,
        isLoading,
        isValidating,
        error,
        mutate: refreshDeliveries,
    } = useSWR(
        [
            'webhook-deliveries',
            alertRuleId,
            alertRuleActionId,
            page,
            statuses.join(','),
            startTime,
            endTime,
        ],
        () =>
            EngineService.getWebhookDeliveries(alertRuleId, alertRuleActionId, {
                page,
                size: PAGE_SIZE,
                status: statuses.length > 0 ? statuses : undefined,
                startTime,
                endTime,
            }),
        { keepPreviousData: true, revalidateOnFocus: false },
    );

    const firstDeliveryIdsToNewUrl = useMemo(
        () => findFirstDeliveriesToNewUrl(data?.items ?? []),
        [data?.items],
    );

    const columns: TableColumn<WebhookDelivery>[] = [
        {
            id: 'expand',
            header: '',
            cell: ({ isExpanded, toggleExpand }) => (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleExpand?.();
                    }}
                    aria-label={
                        isExpanded
                            ? t('webhookDeliveries.collapseDetails')
                            : t('webhookDeliveries.expandDetails')
                    }
                    aria-expanded={!!isExpanded}
                    className="flex items-center justify-center rounded p-1 text-grey-300 hover:bg-surface-hover hover:text-white-100"
                >
                    <CaretRightIcon
                        className={cn(
                            'size-4 transition-transform duration-200',
                            isExpanded && 'rotate-90',
                        )}
                    />
                </button>
            ),
            width: '48px',
        },
        {
            id: 'createdAt',
            header: t('webhookDeliveries.columnTime'),
            cell: ({ row }) => (
                <WebhookDeliveryTimeCell
                    delivery={row.original}
                    isFirstToUrl={firstDeliveryIdsToNewUrl.has(row.original.id)}
                />
            ),
            width: '300px',
        },
        {
            id: 'event',
            header: t('webhookDeliveries.columnEvent'),
            cell: ({ row }) => (
                <WebhookDeliveryEventCell eventId={row.original.eventId} />
            ),
            width: '340px',
        },
        {
            id: 'status',
            header: t('webhookDeliveries.columnStatus'),
            cell: ({ row }) => (
                <span
                    className={
                        row.original.statusCode && row.original.statusCode < 300
                            ? 'text-green-bright'
                            : 'text-danger-200'
                    }
                >
                    {row.original.statusCode ??
                        t('webhookDeliveries.statusNoResponse')}
                </span>
            ),
            width: '100px',
        },
        {
            id: 'latency',
            header: t('webhookDeliveries.columnLatency'),
            cell: ({ row }) =>
                row.original.latencyMs == null
                    ? '—'
                    : formatLatency(row.original.latencyMs),
            width: '100px',
        },
    ];

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={
                <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                        <span>{t('webhookDeliveries.title')}</span>
                        <span className="text-grey-500">·</span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="min-w-0 truncate text-sm text-grey-200">
                                    {webhookUrl}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="break-all">
                                {webhookUrl}
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    <div className="min-w-0 truncate text-xs text-grey-300">
                        {t('webhookDeliveries.alertRuleLabel', {
                            name: alertRuleName,
                        })}
                    </div>
                </div>
            }
            size="large"
            maxWidth="max-w-[1200px]"
            maxHeight="max-h-[calc(100dvh-6rem)]"
        >
            <div className="flex flex-col px-6 pb-6">
                {stats && (
                    <WebhookDeliverySummary
                        stats={stats}
                        selectedStatuses={statuses}
                        onStatusToggle={toggleStatus}
                        filters={
                            <WebhookDeliveryFilters
                                hint={
                                    <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={refresh}
                                                aria-label={t(
                                                    'webhookDeliveries.refresh',
                                                )}
                                                className="flex items-center text-grey-200 transition-colors hover:text-white-100"
                                            >
                                                <ArrowsClockwiseIcon
                                                    size={14}
                                                    className={
                                                        isValidating
                                                            ? 'animate-spin'
                                                            : undefined
                                                    }
                                                />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            {t('webhookDeliveries.refresh')}
                                        </TooltipContent>
                                    </Tooltip>
                                }
                                dateTime={dateTime}
                                resolvedRange={resolvedRange}
                                onDateTimeChange={changeDateTime}
                            />
                        }
                    />
                )}

                {error ? (
                    <div className="text-sm text-danger-200">
                        {t('webhookDeliveries.loadFailed')}
                    </div>
                ) : (
                    <Table<WebhookDelivery>
                        data={data?.items ?? []}
                        columns={columns}
                        isLoading={isLoading}
                        page={page}
                        pageSize={PAGE_SIZE}
                        skeletonRowCount={PAGE_SIZE}
                        totalCount={data?.page.totalItems}
                        onPageChange={setPage}
                        resizableColumns
                        expandableRow={(delivery, isExpanded) => (
                            <WebhookDeliveryDetails
                                alertRuleId={alertRuleId}
                                alertRuleActionId={alertRuleActionId}
                                delivery={delivery}
                                isExpanded={isExpanded}
                            />
                        )}
                        emptyState={{
                            title: hasAnyFilter
                                ? t('webhookDeliveries.emptyFiltered')
                                : t('webhookDeliveries.empty'),
                        }}
                    />
                )}
            </div>
        </Modal>
    );
}
